import { DestroyRef, Injectable, inject } from '@angular/core';
import { Location } from '@angular/common';
import {
  ActivatedRouteSnapshot,
  NavigationEnd,
  NavigationStart,
  Router,
} from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { LocaleService } from '../locale-wrapper/locale.service';
import { LocaleUrlUtil } from '../../utils/locale-url.util';
import { ROUTER_CONSTANTS } from '../../constants/router.constants';
import { SessionStorageUtility } from '../../utils/sessionStorageUtils';

export const NAVIGATION_HISTORY_STORAGE_KEY = 'dcmNavigationHistory';
export const BACK_FALLBACK_ROUTE_DATA_KEY = 'backFallback';
const MAX_ENTRIES = 50;

interface INavigationHistoryEntry {
  url: string;
  locale: string;
}

@Injectable({ providedIn: 'root' })
export class NavigationHistoryService {
  private readonly router = inject(Router);
  private readonly location = inject(Location);
  private readonly localeService = inject(LocaleService);
  private readonly destroyRef = inject(DestroyRef);

  private stack: INavigationHistoryEntry[] = [];
  private initialized = false;
  private isFirstNavigation = true;
  private isTraversal = false;
  private isReplace = false;
  private isSkipped = false;

  init(): void {
    if (this.initialized) {
      return;
    }
    this.initialized = true;
    this.stack = this.restore();

    this.router.events
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        if (event instanceof NavigationStart) {
          const extras = this.router.currentNavigation()?.extras;
          this.isTraversal =
            event.navigationTrigger === 'popstate' ||
            (this.isFirstNavigation &&
              LocaleUrlUtil.getDocumentNavigationType() === 'back_forward');
          this.isReplace = !!extras?.replaceUrl;
          this.isSkipped = !!extras?.skipLocationChange;
        } else if (event instanceof NavigationEnd) {
          this.isFirstNavigation = false;
          this.record(event.urlAfterRedirects);
        }
      });
  }

  get canGoBack(): boolean {
    return this.stack.length > 1;
  }

  back(fallbackUrl = `/${ROUTER_CONSTANTS.dashboard}`): void {
    if (this.canGoBack) {
      const target = this.stack[this.stack.length - 2];
      const currentLocale = this.localeService.getLocale();
      this.stack.length -= 1;
      SessionStorageUtility.set(NAVIGATION_HISTORY_STORAGE_KEY, this.stack);

      if (target.locale === currentLocale) {
        this.location.back();
      } else {
        const baseHref =
          this.localeService.getBaseHrefForLocaleCode(currentLocale);
        window.open(`${baseHref}${target.url}`, '_self');
      }
      return;
    }
    this.router.navigateByUrl(this.getRouteDataFallback() ?? fallbackUrl, {
      replaceUrl: true,
    });
  }

  private record(url: string): void {
    if (this.isSkipped) {
      return;
    }
    const locale = this.localeService.getLocale();
    const top = this.stack[this.stack.length - 1];
    if (top?.url === url) {
      top.locale = locale;
    } else if (this.isTraversal) {
      const index = this.stack.findIndex((entry) => entry.url === url);
      if (index >= 0) {
        this.stack.length = index + 1;
        this.stack[index].locale = locale;
      } else {
        this.stack.push({ url, locale });
      }
    } else if (this.isReplace && this.stack.length) {
      this.stack[this.stack.length - 1] = { url, locale };
    } else {
      this.stack.push({ url, locale });
    }
    if (this.stack.length > MAX_ENTRIES) {
      this.stack.splice(0, this.stack.length - MAX_ENTRIES);
    }
    SessionStorageUtility.set(NAVIGATION_HISTORY_STORAGE_KEY, this.stack);
  }

  private restore(): INavigationHistoryEntry[] {
    if (
      LocaleUrlUtil.getDocumentNavigationType() === 'navigate' &&
      this.isCrossOriginReferrer()
    ) {
      SessionStorageUtility.clearItem(NAVIGATION_HISTORY_STORAGE_KEY);
      return [];
    }
    const stored = SessionStorageUtility.get(NAVIGATION_HISTORY_STORAGE_KEY);
    return Array.isArray(stored)
      ? stored.filter(
          (entry): entry is INavigationHistoryEntry =>
            !!entry &&
            typeof entry.url === 'string' &&
            typeof entry.locale === 'string',
        )
      : [];
  }

  private isCrossOriginReferrer(): boolean {
    if (!document.referrer) {
      return false;
    }
    try {
      return new URL(document.referrer).origin !== window.location.origin;
    } catch {
      return true;
    }
  }

  private getRouteDataFallback(): string | undefined {
    let route: ActivatedRouteSnapshot | null =
      this.router.routerState.snapshot.root;
    let fallback: string | undefined;
    while (route) {
      fallback = route.data?.[BACK_FALLBACK_ROUTE_DATA_KEY] ?? fallback;
      route = route.firstChild;
    }
    return fallback;
  }
}
